// dependencies
import React, { Suspense, lazy, useState } from "react";

import { faSparkles } from "@fortawesome/pro-solid-svg-icons";

import { Icon, Text } from "@performyard/py-components";

// Components
import OptionsDrawer from "../../../../../../components/OptionsDrawer";
import Table from "../../../../../../components/TableLite";

import { useAiSettings } from "../../../../../../api/hooks";
import { QuestionRow, SortableCol } from "./useQuestionTableRows";

import Config from "../../../../Config";
// styles
import css from "./QuestionsTable.module.scss";

const { QUESTION_COLUMN_WIDTH, DISTRIBUTION_COLUMN_WIDTH } = Config.QUESTIONS.TABLE;

const EngagementAnalysisModule = lazy(() =>
	import("../../../../../../components/AIModules/EngagementInsight").then((mod) => ({
		default: mod.EngagementAnalysisModule,
	}))
);

type QuestionsTableProps = {
	rows: QuestionRow[];
	sortField: SortableCol;
	sortDirection: "desc" | "asc";
	handleColumnClick: (columnId: SortableCol, direction: "asc" | "desc") => void;
	handleDownloadQuestionAnswers: (questionId: string) => void;
	getRowInformation: (questionId: string) => {
		questionId: string;
		filter: {
			startDate: Date | null;
			endDate: Date | null;
			value: string;
		};
		cohortId: string;
	};
	handleDownloadNPSAdditionalFeedback: () => void;
	isLoading: boolean;
	emptyChildren?: React.ReactNode;
};

const QuestionsTable = ({
	rows,
	sortField,
	sortDirection,
	handleColumnClick,
	handleDownloadQuestionAnswers,
	getRowInformation,
	handleDownloadNPSAdditionalFeedback,
	isLoading,
	emptyChildren,
}: QuestionsTableProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const [rowInformation, setRowInformation] = useState<{
		questionId: string;
		filter: {
			startDate: Date | null;
			endDate: Date | null;
			value: string;
		};
		cohortId: string;
	} | null>(null);

	const { data: AiSettings } = useAiSettings();

	return (
		<>
			<Table
				theme="vJul23"
				bodyCellClass={css.bodyCell}
				headCellClass={css.headCell}
				activeSort={sortField}
				activeSortDirection={sortDirection}
				onColumnClick={handleColumnClick}
				isLoading={isLoading}
				emptyChildren={emptyChildren}
				columns={[
					{
						id: "factor",
						label: "Factors",
						isSortable: true,
						width: "1fr",
					},
					{
						id: "question_text",
						label: "Questions",
						isSortable: true,
						width: `${QUESTION_COLUMN_WIDTH}px`,
					},
					{
						id: "average_score",
						label: "Average Score",
						isSortable: true,
						width: "1fr",
					},
					{
						id: "distribution",
						label: "Distribution of Ratings",
						isSortable: false,
						width: `${DISTRIBUTION_COLUMN_WIDTH}px`,
					},
					{
						id: "total_response_count",
						label: "# of Responses",
						isSortable: true,
						width: "1fr",
					},
					{
						id: "options",
						label: "",
						width: "2%",
						isSortable: false,
						getter: (row) => {
							const options: { id: string; label: string | React.ReactNode }[] = [
								{ id: "download_csv", label: "Download CSV" },
							];

							const isAIAnalysisEnabled = AiSettings?.engagementAnalysisEnabled;

							const shouldShowAIAnalysis =
								isAIAnalysisEnabled &&
								(row.scale === "text" || row.question_id === "eNPSAdditionalFeedback");

							if (shouldShowAIAnalysis) {
								options.push({
									id: "ai_analysis",
									label: (
										<div
											data-testid="ai-analysis-button"
											className="flex items-center justify-center gap-1"
										>
											<Icon icon={faSparkles} />
											<Text size="caption" weight="regular" color="neutral-5">
												Summarize
											</Text>
										</div>
									),
								});
							}

							// Guard: Only show OptionsDrawer for relevant rows
							if (row.scale === "text" || row.question_id === "eNPSAdditionalFeedback") {
								return (
									<OptionsDrawer
										openOn="bottom"
										horizontal
										options={options}
										onSelect={(option) => {
											if (option.id === "download_csv") {
												if (row.question_id === "eNPSAdditionalFeedback") {
													handleDownloadNPSAdditionalFeedback();
												} else {
													handleDownloadQuestionAnswers(row.question_id);
												}
											} else if (option.id === "ai_analysis") {
												setIsOpen(true);
												const rowInfo = getRowInformation(row.question_id);
												setRowInformation(rowInfo);
											}
										}}
									/>
								);
							}

							return null; // or undefined, if nothing should render
						},
					},
				]}
				rows={rows}
			/>
			{isOpen && rowInformation && (
				<Suspense
					fallback={
						<div className="fixed bottom-0 right-0 z-50 m-4 rounded bg-white p-4 shadow">
							<p className="text-sm text-neutral-600">Loading analysis...</p>
						</div>
					}
				>
					<EngagementAnalysisModule
						isOpen={isOpen}
						onOpenChange={setIsOpen}
						selectedCohort={rowInformation.cohortId}
						filter={rowInformation.filter}
						questionId={rowInformation.questionId}
					/>
				</Suspense>
			)}
		</>
	);
};

export default QuestionsTable;
